import * as z from "zod";
import {
  array_field,
  checkbox_field,
  nullable_field,
  required_date_field,
  timeString,
} from "../_helpers";

const grades_field = array_field(z.uuid("Please select valid Grades")).refine(
  (arr) => arr.length >= 1,
  "Please select at least one Grade",
);
const subjects_field = array_field(
  z.uuid("Please select valid Subjects"),
).refine((arr) => arr.length >= 1, "Please select at least one Subject");

const shift_type_enum = z.enum(
  ["full_day", "morning", "afternoon", "custom"],
  { error: "Please select a valid Shift Type" },
);
const review_status_enum = z.enum(
  ["ready_to_review", "approved", "rejected"],
  { error: "Please select a valid Review Status" },
);
const pay_status_enum = z.enum(["unpaid", "paid"], {
  error: "Please select a valid Pay Status",
});

// Dates are YYYY-MM-DD strings; parse as UTC so the weekday is unambiguous.
const DAY_MS = 86_400_000;
const isWeekend = (date: string) => {
  const day = new Date(`${date}T00:00:00Z`).getUTCDay();
  return day === 0 || day === 6;
};
const rangeIncludesWeekend = (start: string, end: string) => {
  const startMs = Date.parse(`${start}T00:00:00Z`);
  const endMs = Date.parse(`${end}T00:00:00Z`);
  if (endMs - startMs >= 5 * DAY_MS) return true;
  for (let t = startMs; t <= endMs; t += DAY_MS) {
    if (isWeekend(new Date(t).toISOString().slice(0, 10))) return true;
  }
  return false;
};

export const job_opening_schema = z
  .object({
    school: z.uuid("Please select a valid School"),
    multi_day: checkbox_field,
    draft: checkbox_field,
    custom_time: checkbox_field,
    start_date: required_date_field("Start Date"),
    end_date: nullable_field(z.iso.date("End Date is not a valid date")),
    grades: grades_field,
    subjects: subjects_field,
    shift_type: nullable_field(shift_type_enum),
    start_time: nullable_field(timeString("Start Time")),
    end_time: nullable_field(timeString("End Time")),
    pay_rate: nullable_field(
      z.coerce
        .number({ error: "Pay Rate is not a valid number" })
        .nonnegative("Pay Rate cannot be negative"),
    ),
    teacher_name: nullable_field(z.string()),
    notes: nullable_field(z.string()),
    review_status: nullable_field(review_status_enum),
    pay_status: nullable_field(pay_status_enum),
  })
  .refine((d) => !d.multi_day || !!d.end_date, {
    path: ["end_date"],
    message: "End Date is required for multi-day jobs",
  })
  .refine((d) => !d.multi_day || !d.end_date || d.end_date >= d.start_date, {
    path: ["end_date"],
    message: "End Date must be on or after Start Date",
  })
  .refine((d) => !isWeekend(d.start_date), {
    path: ["start_date"],
    message: "Start Date cannot be on a weekend",
  })
  .refine((d) => !d.multi_day || !d.end_date || !isWeekend(d.end_date), {
    path: ["end_date"],
    message: "End Date cannot be on a weekend",
  })
  .refine(
    (d) =>
      !d.multi_day || !d.end_date ||
      !rangeIncludesWeekend(d.start_date, d.end_date),
    {
      path: ["end_date"],
      message: "Date range cannot include weekend days",
    },
  );
